import pino from 'pino';

/**
 * Log levels supported by the logger.
 */
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Logger interface for type safety.
 */
export type Logger = pino.Logger;

/**
 * Logger configuration options.
 */
interface LoggerOptions {
  name: string;
  level?: LogLevel;
  browser?: boolean;
}

/**
 * Creates a configured pino logger instance.
 *
 * @param options - Logger configuration
 * @returns Configured pino logger
 *
 * @example
 * ```typescript
 * const logger = createLogger({ name: 'server', level: 'info' });
 * logger.info({ userId: '123' }, 'User logged in');
 * ```
 */
export function createLogger(options: LoggerOptions): Logger {
  const { name, level = 'info', browser = false } = options;

  if (browser) {
    // Browser-friendly configuration with pretty printing
    return pino({
      name,
      level,
      browser: {
        asObject: true,
      },
    });
  }

  // Server configuration with structured JSON output
  return pino({
    name,
    level,
    formatters: {
      level: (label) => ({ level: label }),
    },
    timestamp: pino.stdTimeFunctions.isoTime,
  });
}
